import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { TranslationService } from '@ess-front/shared/translation';
import { WINDOW } from '@ess-front/shared/tokens';
import { getBrowserLang, TranslocoService } from '@jsverse/transloco';
import { TranslationApiService } from '@shared/api/translation-api.service';
import { map, Observable, take, tap } from 'rxjs';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { Title } from '@angular/platform-browser';
import { SupportedLanguage } from '@app/utils/constants/supported-languages';

@Injectable({
  providedIn: 'root',
})
export class TranslationDomainService extends TranslationService {
  constructor(
    private translocoService: TranslocoService,
    private translationApiService: TranslationApiService,
    private dateAdapter: DateAdapter<Date>,
    private title: Title,
    @Inject(MAT_DATE_LOCALE) private matDateLocale: string,
    @Inject(WINDOW) private window: Window,
    @Inject(DOCUMENT) private document: Document,
  ) {
    super();
  }

  get langChange$(): Observable<string> {
    return this.translocoService.langChanges$;
  }

  loadTranslations$(lang: SupportedLanguage): Observable<void> {
    return this.translocoService.load(lang).pipe(map(() => undefined));
  }

  translate(key: string, params?: Record<string, unknown>, lang?: SupportedLanguage): string {
    return this.translocoService.translate(key, params, lang);
  }

  translateObject<T extends object>(obj: T, properties: keyof T | (keyof T)[]): T {
    if (Array.isArray(properties)) {
      let newObj = { ...obj };
      properties.forEach(property => {
        newObj = { ...newObj, [property]: this.translate(`${obj[property]}`) };
      });
      return newObj;
    }
    return { ...obj, [properties]: this.translate(`${obj[properties]}`) };
  }

  translateObjectArray<T extends object>(objArray: T[], property: keyof T | (keyof T)[]): T[] {
    return objArray.map(obj => this.translateObject(obj, property));
  }

  getUserLanguageByHash$(userHash: string): Observable<string> {
    return this.translationApiService.getUserLanguageByHash$(userHash);
  }

  getTripLanguage$(tripHash: string): Observable<string> {
    return this.translationApiService.getTripLanguage$(tripHash);
  }

  getBrowserLanguage(): string {
    const lang = getBrowserLang() ?? 'en';

    if (lang === 'zh') {
      return this.resolveChineseVariant();
    }

    return lang;
  }

  getActiveLanguage(): string {
    return this.translocoService.getActiveLang();
  }

  setActiveLanguage(language: string): void {
    this.translocoService.setActiveLang(language);
    this.setDateLocale(language);
    this.document.documentElement.lang = language;
  }

  isLanguageSupported(language: string): boolean {
    return this.translocoService.isLang(language);
  }

  isTranslationLoaded$(): Observable<boolean> {
    return this.translocoService.selectTranslateObject('').pipe(map(() => true));
  }

  setTranslatedPageTitle(title: string): void {
    this.isTranslationLoaded$()
      .pipe(
        tap(() => {
          const pageTitle = this.translate(title);
          this.title.setTitle(pageTitle);
        }),
        take(1),
      )
      .subscribe();
  }

  private resolveChineseVariant(): SupportedLanguage {
    const navigatorLang = this.window.navigator.language.toLowerCase();
    if (
      navigatorLang.includes('hant') ||
      navigatorLang.includes('tw') ||
      navigatorLang.includes('hk') ||
      navigatorLang.includes('mo')
    ) {
      return SupportedLanguage.ZhHant;
    }

    return SupportedLanguage.ZhHans;
  }

  private setDateLocale(locale: string): void {
    this.matDateLocale = locale;
    this.dateAdapter.setLocale(this.matDateLocale);
  }
}
